import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;

export const API_BASE = `${BASE}/api`;

const TOKEN_KEY = "pranava.access_token";
const REFRESH_KEY = "pranava.refresh_token";

export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_KEY),
  set: (access, refresh) => {
    if (access) localStorage.setItem(TOKEN_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

export const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const t = tokenStore.get();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

let isRefreshing = false;
let queue = [];

const flushQueue = (error, token) => {
  queue.forEach(({ resolve, reject }) => (error ? reject(error) : resolve(token)));
  queue = [];
};

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    // ---- Structured 403 handling (matrix denials) ----
    // We silently swallow structured `{detail: "forbidden", ...}` responses.
    // For safe idempotent methods (GET / HEAD) we RESOLVE with a null-data
    // response so downstream callers never see a rejected promise — no toast,
    // no error banner. For mutating verbs we still reject so the developer
    // notices a missing <CanAccess> wrap, but apiErrorToast() below no-ops on
    // the structured forbidden shape so the UI stays clean either way.
    if (error.response && error.response.status === 403) {
      const detail = error.response.data?.detail;
      const structured = detail && typeof detail === "object" && detail.detail === "forbidden";
      if (structured) {
        // eslint-disable-next-line no-console
        console.debug("[rbac] 403 silenced", {
          module: detail.module,
          required: detail.required_action,
          role: detail.your_role,
          method: original?.method,
          url: original?.url,
        });
        const method = String(original?.method || "get").toLowerCase();
        if (method === "get" || method === "head") {
          return {
            data: null,
            status: 403,
            statusText: "Forbidden",
            headers: {},
            config: original,
            request: error.request,
            _rbacSilenced: true,
          };
        }
      }
      throw error;
    }
    if (!error.response || original?._retried) throw error;
    if (error.response.status !== 401) throw error;
    if (original.url && original.url.includes("/auth/")) throw error;

    const refresh = tokenStore.getRefresh();
    if (!refresh) throw error;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        queue.push({
          resolve: (token) => {
            original.headers.Authorization = `Bearer ${token}`;
            original._retried = true;
            resolve(api.request(original));
          },
          reject,
        });
      });
    }

    isRefreshing = true;
    try {
      const { data } = await axios.post(`${API_BASE}/auth/refresh`, { refresh_token: refresh });
      tokenStore.set(data.access_token, data.refresh_token);
      flushQueue(null, data.access_token);
      original.headers.Authorization = `Bearer ${data.access_token}`;
      original._retried = true;
      return api.request(original);
    } catch (e) {
      flushQueue(e, null);
      tokenStore.clear();
      window.location.assign("/login");
      throw e;
    } finally {
      isRefreshing = false;
    }
  }
);

export function apiErrorMessage(err) {
  const d = err?.response?.data?.detail;
  if (d == null) return err?.message || "Something went wrong.";
  if (typeof d === "string") return d;
  // Structured RBAC forbidden body → swallow silently. The UI already hides
  // nav / action buttons the user cannot access; a 403 here means a stray
  // background call and must NOT surface as a red toast.
  if (typeof d === "object" && d.detail === "forbidden") return "";
  if (Array.isArray(d)) {
    return d
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (typeof d === "object" && typeof d.msg === "string") return d.msg;
  if (typeof d === "object" && typeof d.message === "string") return d.message;
  // Never render "[object Object]" — safely stringify.
  try { return JSON.stringify(d); } catch { return err?.message || "Something went wrong."; }
}

/**
 * Toast an API error safely. No-ops on empty string (structured RBAC 403 case),
 * so callers can uniformly use `apiErrorToast(err)` instead of
 * `apiErrorToast(err)` and never accidentally render
 * `"[object Object]"` or an empty red bar.
 */
export function apiErrorToast(err) {
  // Lazy-import toast so this module stays sonner-agnostic.
  // eslint-disable-next-line no-unused-vars
  const _ = null;
  const msg = apiErrorMessage(err);
  if (!msg || !msg.trim()) return;
  import("sonner").then(({ toast }) => toast.error(msg)).catch(() => {});
}
